import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner, Button, Chip, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Tooltip, Avatar } from "@heroui/react";
import { Icon } from "@iconify/react";
import { Typography, UniversalTable, IdView } from "../../../../components";
import {
  OnlyFansIc,
  InstagramIc,
  GoogleIc,
  TwitterIc,
  IcReddit,
  IcAnotherSoc,
  IcTg,
  IcTikTok,
  IcYoutube,
  EmptyContentIc,
  InfoCircle,
  UserCheckIc,
  MoneyIc,
} from "../../../../components/icons";
import { CalendarIcon } from "../../../../components/icons/ComplexIcons";
import { useTranslations } from "../../../../contexts/translation.context";
import { formatTimeAgo } from "../../../../utils/formatTimeAgo";
import { CPLOffer, TrafficSource } from "../../../../api/staff";

interface CPLOffersTableProps {
  offers: CPLOffer[];
  isLoading: boolean;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onStatusChange?: (offerId: string, status: 'active' | 'deactivated') => void;
  onApproveOffer?: (offerId: string) => void;
  onRejectOffer?: (offerId: string) => void;
}

// Helper function to get traffic source display name and icon
const getSourceConfig = (source: TrafficSource): { label: string; icon: React.ReactNode } => {
  const config: Record<TrafficSource, { label: string; icon: React.ReactNode }> = {
    only_fans: { label: 'OnlyFans', icon: <OnlyFansIc size={14} fill="#A8A8B0" /> },
    reddit: { label: 'Reddit', icon: <IcReddit size={14} /> },
    instagram: { label: 'Instagram', icon: <InstagramIc size={14} fill="#A8A8B0" /> },
    tiktok: { label: 'TikTok', icon: <IcTikTok size={14} /> },
    google: { label: 'Google', icon: <GoogleIc size={14} fill="#A8A8B0" /> },
    telegram: { label: 'Telegram', icon: <IcTg size={14} /> },
    twitter: { label: 'Twitter', icon: <TwitterIc size={14} fill="#A8A8B0" /> },
    youtube: { label: 'YouTube', icon: <IcYoutube size={14} /> },
    another: { label: 'Другие', icon: <IcAnotherSoc size={14} /> },
  };
  return config[source] || { label: source, icon: <IcAnotherSoc size={14} /> };
};

// Helper function to get status configuration
const getStatusConfig = (status: string) => {
  switch (status) {
    case 'active':
      return {
        label: 'Активный',
        dotColor: '#17c964',
      };
    case 'deactivated':
      return {
        label: 'Деактивирован',
        dotColor: '#A1A1AA',
      };
    case 'hidden':
      return {
        label: 'Скрыт',
        dotColor: '#71717A',
      };
    case 'moderation':
      return {
        label: 'На модерации',
        dotColor: '#E8A323',
      };
    default:
      return {
        label: status,
        dotColor: '#71717A',
      };
  }
};

// Helper function to get marketer level configuration
const getMarketerLevelConfig = (lvl: string) => {
  switch (lvl.toLowerCase()) {
    case 'new':
      return {
        label: 'New',
        bgColor: 'rgba(23, 201, 100, 0.2)',
        textColor: '#17c964',
      };
    case 'base':
      return {
        label: 'Base',
        bgColor: '#EEEEEF',
        textColor: '#71717A',
      };
    case 'pro':
      return {
        label: 'PRO',
        bgColor: '#11181C',
        textColor: '#FFFFFF',
      };
    case 'expert':
      return {
        label: 'Expert',
        bgColor: 'rgba(120, 40, 200, 0.2)',
        textColor: '#7828c8',
      };
    default:
      return {
        label: lvl,
        bgColor: '#EEEEEF',
        textColor: '#71717A',
      };
  }
};

// Helper function to format campaign types
const formatCampaignTypes = (types?: ('free' | 'free_trial' | 'paid')[]): string => {
  if (!types || types.length === 0) {
    return 'N/A';
  }

  const formattedTypes = types.map(type => {
    switch (type) {
      case 'free':
        return 'Free';
      case 'free_trial':
        return 'Free-Trial';
      case 'paid':
        return 'Paid';
      default:
        return type;
    }
  });

  return formattedTypes.join(', ');
};

/**
 * CPLOffersTable Component
 *
 * Main table component for displaying CPL offers.
 * Uses UniversalTable from the component library and custom cell renderers.
 */
const CPLOffersTable: React.FC<CPLOffersTableProps> = ({
  offers,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onStatusChange,
  onApproveOffer,
  onRejectOffer,
}) => {
  const { t } = useTranslations();
  const navigate = useNavigate();

  const handleAction = (action: string, offerId: string) => {
    switch (action) {
      case 'deactivate':
        onStatusChange?.(offerId, 'deactivated');
        break;
      case 'activate':
        onStatusChange?.(offerId, 'active');
        break;
      case 'approve':
        onApproveOffer?.(offerId);
        break;
      case 'reject':
        onRejectOffer?.(offerId);
        break;
    }
  };

  // Define table columns
  const columns = [
    {
      key: "id",
      name: "ID",
      sortable: false
    },
    {
      key: "marketer",
      name: "Маркетолог",
      sortable: false
    },
    {
      key: "source",
      name: "Источник трафика",
      sortable: false
    },
    {
      key: "status",
      name: "Статус",
      sortable: false
    },
    {
      key: "createdAt",
      name: "Дата создания",
      sortable: false
    },
    {
      key: "actions",
      name: "",
      sortable: false
    }
  ];

  const renderCell = useCallback((offer: CPLOffer, columnKey: string) => {
    switch (columnKey) {
      case "id":
        return (
          <IdView textToCopy={offer.id}>
            {offer.id.slice(-4)}
          </IdView>
        );

      case "marketer":
        const { partner } = offer;
        const avatarUrl = partner.avatar;
        const levelConfig = getMarketerLevelConfig(partner.lvl);

        return (
          <div className="flex items-center gap-[16px]">

            <Avatar
              src={avatarUrl}
              showFallback name={partner.name?.slice(0, 1)}
              className="w-[40px] h-[40px] rounded-[12px]"
            />
            <div className="flex flex-col gap-[4px]">
              <div className="flex items-center gap-[4px]">
                <Typography
                  variant="body1"
                  className="font-semibold !text-[#006FEE] cursor-pointer hover:underline"
                  onClick={() => partner.id && navigate(`/users/partners/${partner.id}/balance`)}
                >
                  {partner.name}
                </Typography>
                <Chip
                  className="h-[24px] px-[8px] text-[12px] font-normal"
                  style={{
                    backgroundColor: levelConfig.bgColor,
                    color: levelConfig.textColor,
                  }}
                >
                  {levelConfig.label}
                </Chip>
                <Tooltip
                  content={
                    <div className="px-3 py-2">
                      <div className="text-[12px] font-medium text-[#11181C] mb-2">Данные оффера</div>
                      <Typography variant="label3" className="!font-semibold !text-[#11181C]">
                        {formatCampaignTypes(offer.campaign_types)}
                      </Typography>
                      <div className="flex items-center gap-1 text-[11px] text-[#71717A] mt-1">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 2.5V6H9" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M6 10C8.20914 10 10 8.20914 10 6C10 3.79086 8.20914 2 6 2C3.79086 2 2 3.79086 2 6C2 8.20914 3.79086 10 6 10Z" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>{formatTimeAgo(offer.created_at, t)}</span>
                      </div>
                      {offer.subs && offer.subs.length > 0 && (
                        <div className="flex items-center gap-1 text-[11px] text-[#71717A] mt-1">
                          <MoneyIc/>
                          <span>${parseFloat(offer.subs[0].price).toFixed(2)} /фан</span>
                        </div>
                      )}
                      {offer.subs_min !== undefined && offer.subs_max !== undefined && (
                        <div className="flex items-center gap-1 text-[11px] text-[#71717A] mt-1">
                          <UserCheckIc/>
                          <span>{offer.subs_min}-{offer.subs_max}</span>
                        </div>
                      )}
                    </div>
                  }
                  placement="right"
                  classNames={{
                    base: "py-0 px-0",
                    content: "py-0 px-0 bg-white shadow-lg rounded-lg border border-[#E4E4E7]"
                  }}
                >
                  <div className="cursor-pointer">
                    <InfoCircle/>
                  </div>
                </Tooltip>
              </div>
            </div>
          </div>
        );

      case "source":
        const sourceConfig = getSourceConfig(offer.source);
        return (
          <div className="flex items-center gap-[6px] border-[#D4D4D8] border-[1.4px] h-[24px] px-[8px] rounded-xl w-fit bg-transparent">
            <div className="w-[14px] h-[14px] flex items-center justify-center">
              {sourceConfig.icon}
            </div>
            <Typography variant="body3" className="text-[12px]">
              {sourceConfig.label}
            </Typography>
          </div>
        );

      case "status":
        const statusConfig = getStatusConfig(offer.status);
        return (
          <Chip
            className="h-[24px] px-[4px] text-[12px] font-normal bg-zinc-100"
            style={{
              backgroundColor: '#F4F4F5',
              color: '#11181C',
            }}
          >
            <div className="flex items-center gap-[4px]">
              <div
                className="w-[7px] h-[7px] rounded-full"
                style={{ backgroundColor: statusConfig.dotColor }}
              />
              <span>{statusConfig.label}</span>
            </div>
          </Chip>
        );

      case "createdAt":
        return (
          <div className="flex flex-col gap-[8px]">
            <Tooltip
              content={(
                <div className='flex flex-col'>
                  <Typography variant='body3' className="text-[#71717A]">
                    Дата создания:
                  </Typography>
                  <Typography variant='body2'>
                    {formatTimeAgo(offer.created_at, t)}
                  </Typography>
                </div>
              )}
              placement="top"
            >
              <div className="flex items-center gap-[4px]">
                <CalendarIcon size={14} className="text-gray-600" />
                <Typography variant="body3" className="text-gray-600">
                  {formatTimeAgo(offer.created_at, t)}
                </Typography>
              </div>
            </Tooltip>
          </div>
        );

      case "actions":
        return (
          <Dropdown>
            <DropdownTrigger>
              <Button
                variant="light"
                isIconOnly
                className="min-w-[32px] w-[32px] h-[32px]"
              >
                <Icon icon="solar:menu-dots-bold" width={20} height={20} />
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="Действия"
              onAction={(key: React.Key) => handleAction(String(key), offer.id)}
            >
              {offer.status === 'moderation' ? (
                <>
                  <DropdownItem key="approve" className="text-success" color="success">
                    Одобрить
                  </DropdownItem>
                  <DropdownItem key="reject" className="text-danger" color="danger">
                    Отклонить
                  </DropdownItem>
                </>
              ) : offer.status === 'active' ? (
                <DropdownItem key="deactivate" className="text-danger" color="danger">
                  Деактивировать
                </DropdownItem>
              ) : (
                <DropdownItem key="activate" className="text-success" color="success">
                  Активировать
                </DropdownItem>
              )}
            </DropdownMenu>
          </Dropdown>
        );

      default:
        return null;
    }
  }, []);

  // Empty content component
  const emptyContent = (
    <div className="flex flex-col items-center justify-center py-[80px] px-[24px]">
      <div className="mb-[16px]">
        <EmptyContentIc/>
      </div>
      <div className="text-center">
        <Typography variant="h6" className="text-[#11181C] mb-[8px]">
          {t('admin.cplOffers.empty.title')}
        </Typography>
        <Typography variant="body2" className="text-[#71717A]">
          {t('admin.cplOffers.empty.description')}
        </Typography>
      </div>
    </div>
  );

  if (isLoading && offers.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  return (
    <UniversalTable
      data={offers}
      columns={columns}
      renderCell={renderCell}
      emptyContent={emptyContent}
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
    />
  );
};

export default CPLOffersTable;
